import { ConfirmDialog } from '@/components/feature/confirm-dialog';
import { Chip } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { blueGrey } from '@mui/material/colors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getEmployees } from '../../../api/apiEmployees';
import {
  createEditTask,
  deleteTask,
  getTasks,
  swapRowDown,
  swapRowUp,
} from '../../../api/apiTaskManager';
import './CheckIn.scss';

const EmployeeCheckIn = () => {
  const queryClient = useQueryClient(); // Correctly get the QueryClient instance
  const [records, setRecords] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Fetch employees from the database
  const {
    isLoading: isLoadingEmployees,
    data: employees,
    error: errorEmployees,
  } = useQuery({
    queryKey: ['employees'],
    queryFn: getEmployees,
  });

  // Fetch taskManager from the database
  const {
    isLoading: isLoadingTasks,
    data: tasksData,
    error: errorTasks,
  } = useQuery({
    queryKey: ['taskManager'],
    queryFn: getTasks,
  });

  const { mutate: createTask, isLoading: isCreating } = useMutation({
    mutationFn: createEditTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskManager'] }); // Correct usage of queryClient
      toast.success('New employee has been added');
    },
    onError: error => {
      console.error('Error creating task:', error); // Log the full error for debugging
      toast.error(error.message || 'An error occurred while creating the task');
    },
  });

  // Mutation for deleting a task
  const { mutate: removeTask } = useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskManager'] }); // Refresh the task list after deletion
      toast.success('Task has been deleted');
    },
    onError: error => {
      console.error('Error deleting task:', error);
      toast.error(error.message || 'An error occurred while deleting the task');
    },
  });

  // Initialize the records state with tasksData once it's loaded
  useEffect(() => {
    if (tasksData) {
      setRecords(tasksData);
    }
  }, [tasksData]);

  // Handle loading and error states
  if (isLoadingEmployees || isLoadingTasks) {
    return <div>Loading...</div>;
  }

  if (errorEmployees) {
    toast.error(errorEmployees.message || 'Error loading employees');
    return <div>Error loading employees</div>;
  }

  if (errorTasks) {
    toast.error(errorTasks.message || 'Error loading tasks');
    return <div>Error loading tasks</div>;
  }

  if (isCreating) {
    // Optionally, display some loading indicator or message when creating a task
    console.log('Creating a new task...');
  }

  // Handle moving a row up and updating the database
  const handleMoveUp = async index => {
    if (index === 0) return; // Prevent moving the first row up
    const currentRow = records[index];
    const aboveRow = records[index - 1];

    try {
      await swapRowUp(currentRow, aboveRow); // Swap the rows in the database
      const newRecords = [...records];
      [newRecords[index], newRecords[index - 1]] = [newRecords[index - 1], newRecords[index]];
      setRecords(newRecords);
      queryClient.invalidateQueries({ queryKey: ['taskManager'] }); // Refresh the task list
      toast.success('Row moved up successfully');
    } catch (error) {
      console.error('Error moving row up:', error);
      toast.error('Error moving row up');
    }
  };

  // Handle moving a row down and updating the database
  const handleMoveDown = async index => {
    if (index === records.length - 1) return; // Prevent moving the last row down
    const currentRow = records[index];
    const belowRow = records[index + 1];

    try {
      await swapRowDown(currentRow, belowRow); // Swap the rows in the database
      const newRecords = [...records];
      [newRecords[index], newRecords[index + 1]] = [newRecords[index + 1], newRecords[index]];
      setRecords(newRecords);
      queryClient.invalidateQueries({ queryKey: ['taskManager'] }); // Refresh the task list
      toast.success('Row moved down successfully');
    } catch (error) {
      console.error('Error moving row down:', error);
      toast.error('Error moving row down');
    }
  };

  const handleAddRecord = employee => {
    // Find the corresponding task data for the employee
    const employeeTask = tasksData.find(task => task.name === employee.empName);

    // Template for the new T column
    const taskTemplate = JSON.stringify({
      serTitle: '',
      serDescription: '',
      serCredit: 0,
      serSupply: 0,
      serStatus: true,
      serAmount: null,
      serStation: null,
      serNote: null,
      serSpecialCategory: [],
      serCategory: [],
      serAvatar: {
        shape: 'square',
        color1: 'transparent',
        color2: 'transparent',
      },
    });

    const newRecord = {
      avatar: employee.empAvatar || '/puritynailbar_svg.png',
      name: employee.empName,
      employeeCategory: employee.empCategory,
      employeeSpecialCategory: employee.empSpecialCategory,
      empUserId: employee.empUserId,
      income: 0,
      credits: 0,
      supplies: 0,
      notify: '',
      status: '',
      checkinTime: new Date().toLocaleString(),
    };

    // Dynamically create t1 to t10 properties as objects
    for (let i = 1; i <= 20; i++) {
      const taskKey = `t${i}`;
      newRecord[taskKey] = employeeTask?.[taskKey]
        ? employeeTask[taskKey]
        : JSON.parse(taskTemplate);
    }

    createTask(newRecord);
    setRecords([...records, newRecord]);
    // add the new row to the database
  };

  console.log(records);

  const handleDeleteClick = index => {
    setDeleteIndex(index);
    setRecordToDelete(records[index]);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null && recordToDelete) {
      removeTask(recordToDelete.id);
      const updatedRecords = records.filter((_, i) => i !== deleteIndex);
      setRecords(updatedRecords);
      setDeleteDialogOpen(false);
      setDeleteIndex(null);
      setRecordToDelete(null);
    }
  };

  // Render employee buttons, only show employees with empStatus === true
  const renderButtons = () => {
    return employees
      .filter(
        employee =>
          employee.empStatus === true && // Only include employees with empStatus === true
          !records.some(record => record.name === employee.empName)
      )
      .map(employee => (
        <button
          key={employee.id}
          onClick={() => handleAddRecord(employee)}
          className="employee-btn"
        >
          {employee.empName.toUpperCase()}
        </button>
      ));
  };

  const renderRecords = () => {
    return records.map((record, index) => (
      <tr key={index}>
        {/* <td>{record.id}</td> */}
        <td>
          <div className="employee-detail">
            <Avatar
              src={record.avatar || '/puritynailbar_svg.png'}
              alt={record.name}
              sx={{ width: 45, height: 45 }}
            />

            <div>
              <div>{record.name.toUpperCase()}</div>
              <div className="category">
                {record.employeeCategory.map((category, index) => (
                  <Chip
                    key={index}
                    label={category}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '8px', // Set text size to 10px
                      color: blueGrey[800], // Set text color to blueGrey[500]
                      backgroundColor: 'rgba(255, 159, 15, 0.3)', // Cyan background with 50% opacity
                      border: '1px solid rgba(222, 207, 148, 0.5)', // Optional: match border color
                      /* padding: "0px 1px",  */
                      height: '14px', // Control chip height to make it thinner
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </td>
        <td>
          <div className="login-time">{record.checkinTime.replace('T', ' ')}</div>
        </td>
        {/* <td>{record.credits}</td> */}
        <td>
          <div className="login-actions">
            <button className="up" onClick={() => handleMoveUp(index)}>
              &uarr;
            </button>
            <button className="down" onClick={() => handleMoveDown(index)}>
              &darr;
            </button>
            <button className="delete" onClick={() => handleDeleteClick(index)}>
              Delete
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="checkin-container">
      <div className="employee-buttons">{renderButtons()}</div>
      <table className="checkin-table">
        <thead>
          <tr>
            {/* <th>ID</th> */}
            <th>EMPLOYEE</th>
            <th>TIME</th>
            <th>UPDATE</th>
          </tr>
        </thead>
        <tbody>{renderRecords()}</tbody>
      </table>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirm Delete"
        description={`Are you sure you want to delete '${recordToDelete?.name || ''}' check-in record? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default EmployeeCheckIn;
